import React, { useState, useEffect } from "react";
import { Button, Input, notification, Tag, Drawer } from "antd";
import { Gamepad2 } from "lucide-react";
import { TagOutlined, CloseOutlined } from "@ant-design/icons";
import {
  fetchProviderGame,
  fetchDownloadProduct,
  processGamePayment,
  getVouchersByOrder,
} from "../../services/topupService";
import { triggerMiniAppPayment } from "../../services/paymentUtils";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const GameCard = ({ setLoading, loading, token }) => {
  const [providerGame, setProviderGame] = useState([]);
  const [cardGameInfo, setCardGameInfo] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailError, setEmailError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [voucherValue, setVoucherValue] = useState(0);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherTxt, setVoucherTxt] = useState("");
  const [openVoucher, setOpenVoucher] = useState(false);
  const [vouchers, setVouchers] = useState([]);

  const formatVND = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      setEmailError("Vui lòng nhập email");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Email không hợp lệ");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleProviderSelect = async (game) => {
    setSelectedProvider(game);
    setSelectedCard(null);
    setEmailRecipient("");
    setEmailError("");
    setQuantity(1);
    setVoucherCode("");
    setVoucherValue(0);
    setVoucherTxt("");
    setLoading(true);
    try {
      const res = await fetchDownloadProduct(game.providercode);
      setCardGameInfo(res?.data?.data || []);
    } catch (error) {
      setCardGameInfo([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCardSelect = (card) => {
    setSelectedCard(card);
    setQuantity(1);
  };

  const getVouchers = async () => {
    if (!selectedCard) return;
    setLoading(true);
    try {
      const res = await getVouchersByOrder({
        Quantity: quantity,
        Total: selectedCard.productamount * quantity,
      });
      setVouchers(res?.data?.data || []);
    } catch (error) {
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  const applyVoucherPick = async (voucher) => {
    let amount = voucher.amountvoucher;
    if (voucher.discounttype === "PERCENTAGE") {
      const amountDiscount = Math.ceil(
        selectedCard.productamount * quantity * (voucher.discountvalue / 100)
      );
      amount = amountDiscount < voucher.maxdiscountvalue ? amountDiscount : voucher.maxdiscountvalue;
    }
    setVoucherValue(amount);
    setVoucherCode(voucher.code);
    setVoucherTxt(
      `<div class="text-[14px] text-green-600 font-medium">Đã áp dụng mã giảm giá: ${formatVND(amount)}</div>`
    );
    setOpenVoucher(false);
  };

  const handlePayment = async () => {
    if (!selectedCard || !validateEmail(emailRecipient)) return;
    setLoading(true);
    try {
      const paymentData = {
        ProviderCode: selectedProvider?.providercode,
        Amount: Math.max(0, selectedCard.productamount * quantity - voucherValue),
        ProductId: selectedCard.productid,
        AmountItem: selectedCard.productamount,
        Quantity: quantity,
        EmailRecipient: emailRecipient.trim(),
        VoucherCode: voucherCode.trim(),
      };
      paymentData.InfoOrder = JSON.stringify({
        Service: "gamecard",
        Sponsor: "IPASS",
        ProviderName: selectedProvider?.providername,
        Amount: paymentData.Amount,
        Email: paymentData.EmailRecipient,
        Quantity: paymentData.Quantity,
        VoucherCode: voucherCode.trim() || "",
        VoucherValue: voucherValue || 0,
        AmountItem: selectedCard.productamount,
      });
      const paymentResponse = await processGamePayment(paymentData);
      if (paymentResponse?.data?.data?.ordercode) {
        await triggerMiniAppPayment(paymentResponse.data.data.ordercode, paymentData.Amount);
      }
    } catch (error) {
      notification.error({
        message: 'Thanh toán thất bại',
        description: error.response?.data?.message || 'Đã xảy ra lỗi khi thanh toán',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cardGameInfo.length > 0) {
      setSelectedCard(cardGameInfo[0]);
    }
  }, [cardGameInfo]);

  useEffect(() => {
    if (token) {
      const loadProviders = async () => {
        setLoading(true);
        try {
          const res = await fetchProviderGame();
          const data = (res?.data?.data || []).filter((n) => n.status === 1);
          setProviderGame(data);
          if (data.length > 0) {
            handleProviderSelect(data[0]);
          }
        } catch (error) {
          setProviderGame([]);
        } finally {
          setLoading(false);
        }
      };
      loadProviders();
    }
  }, [token]);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      {providerGame.length > 0 && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Danh sách thẻ Game
          </label>
          <div className="grid grid-cols-4 gap-2">
            {providerGame.map((game) => {
              const isSelected =
                selectedProvider?.providercode === game.providercode;
              return (
                <Button
                  key={game.providercode}
                  onClick={() => handleProviderSelect(game)}
                  ghost={true}
                  className={`py-3 px-2 rounded-xl flex flex-col items-center gap-2 h-auto ${isSelected
                    ? "!bg-primary-50 !border !border-primary !text-primary shadow-sm"
                    : "!bg-white !border !border-gray-200 !text-gray-500 hover:!border-primary hover:!text-primary"
                    }`}
                >
                  <div className="w-15 h-14 flex items-center justify-center">
                    {game.link ? (
                      <img
                        src={`${BASE_URL}${game.link}`}
                        alt={game.providername}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Gamepad2 size={24} className="text-gray-400" />
                    )}
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {cardGameInfo.length > 0 && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Chọn mệnh giá thẻ
          </label>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {cardGameInfo.map((card) => {
              const isSelected = selectedCard?.productid === card.productid;
              return (
                <Button
                  key={card.productid}
                  onClick={() => handleCardSelect(card)}
                  type={isSelected ? "primary" : "default"}
                  className="py-3 px-4 rounded-xl flex justify-between items-center h-auto"
                >
                  <span className="font-bold">
                    {formatVND(card.productamount)}
                  </span>
                </Button>
              );
            })}
          </div>

          {selectedCard && (
            <>
              <div className="border-t border-gray-100 pt-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-semibold text-gray-700">
                    Mã khuyến mãi
                  </span>
                  <div className="flex items-center gap-2">
                    <Tag
                      onClick={() => {
                        setOpenVoucher(true);
                        getVouchers();
                      }}
                      color={voucherCode === "" && voucherValue === 0 ? "default" : "success"}
                      icon={<TagOutlined />}
                      className="cursor-pointer"
                    >
                      {voucherCode === "" && voucherValue === 0
                        ? "Chọn mã giảm giá"
                        : voucherCode}
                    </Tag>
                    {voucherCode !== "" && voucherValue > 0 && (
                      <CloseOutlined
                        onClick={() => {
                          setVoucherCode("");
                          setVoucherValue(0);
                          setVoucherTxt("");
                        }}
                        style={{ fontSize: 16, color: "red", cursor: "pointer" }}
                      />
                    )}
                  </div>
                </div>
                {voucherTxt && (
                  <div
                    className="mb-3 text-sm"
                    dangerouslySetInnerHTML={{ __html: voucherTxt }}
                  />
                )}

                <div className="space-y-1.5 text-sm">
                  {voucherValue > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Giảm giá</span>
                      <span className="text-green-600 font-medium">-{formatVND(voucherValue)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-100 pt-1.5">
                    <span className="font-semibold text-gray-800">Thành tiền</span>
                    <span className="font-bold text-primary-600">{formatVND(Math.max(0, selectedCard.productamount * quantity - voucherValue))}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {cardGameInfo.length === 0 && selectedProvider && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-6">
          <div className="text-center py-6 text-gray-400 text-sm">
            Không có mệnh giá nào
          </div>
        </div>
      )}

      {selectedCard && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Số lượng thẻ
          </label>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
              className="!w-10 !h-10 !min-w-0 !p-0 flex items-center justify-center !text-base font-bold"
            >
              -
            </Button>
            <span className="font-bold text-lg w-8 text-center">{quantity}</span>
            <Button
              onClick={() => setQuantity(quantity + 1)}
              className="!w-10 !h-10 !min-w-0 !p-0 flex items-center justify-center !text-base font-bold"
            >
              +
            </Button>
            <span className="ml-2 text-sm text-gray-500">
              Thành tiền: <span className="font-semibold text-gray-800">{formatVND(Math.max(0, selectedCard.productamount * quantity - voucherValue))}</span>
            </span>
          </div>
        </div>
      )}

      {selectedCard && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Email nhận thẻ <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Nhập email nhận thẻ"
            value={emailRecipient}
            onChange={(e) => {
              setEmailRecipient(e.target.value);
              if (emailError) validateEmail(e.target.value);
            }}
            onBlur={() => validateEmail(emailRecipient)}
            status={emailError ? "error" : ""}
            className="rounded-xl"
          />
          {emailError && (
            <p className="text-red-500 text-xs mt-1">{emailError}</p>
          )}
        </div>
      )}

      <Drawer
        title={
          <div className="flex flex-col gap-4">
            <div className="flex justify-between">
              <span className="uppercase font-bold text-[20px]">
                Mã giảm giá của tôi
              </span>
              <CloseOutlined
                onClick={() => {
                  setOpenVoucher(false);
                  setVoucherTxt("");
                }}
                style={{ fontSize: 18, cursor: "pointer" }}
              />
            </div>
          </div>
        }
        placement={"bottom"}
        closable={false}
        onClose={() => {
          setOpenVoucher(false);
          setVoucherTxt("");
        }}
        open={openVoucher}
        height={"100%"}
        bodyStyle={{
          background: "#f3f6f6",
        }}
      >
        <div className="p-4">
          {vouchers.map((voucher) => (
            <div
              key={voucher.id}
              style={{
                boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
              }}
              className="rounded-[14px] bg-white p-3 mb-4"
            >
              <div className="flex gap-4 w-full">
                <div className="w-1/5 flex items-center justify-center">
                  <img
                    src="/images/payment_card.png"
                    className="w-[32px]"
                    alt=""
                  />
                </div>
                <div className="border-lime-300 border-1 flex flex-col gap-1 flex-1">
                  <div className="font-semibold text-[16px] text-primary">
                    Mã {voucher.code}
                  </div>
                  <div className="text-red-600 text-[14px] font-bold">
                    {voucher.title}
                  </div>
                  <div className="text-[14px]">
                    Giảm {voucher.discounttype === "PERCENTAGE" ? `${voucher.discountvalue}%` : formatVND(voucher.amountvoucher)}
                    {voucher.maxdiscountvalue && voucher.discounttype === "PERCENTAGE" && (
                      <span className="text-gray-500"> (tối đa {formatVND(voucher.maxdiscountvalue)})</span>
                    )}
                  </div>
                  <div
                    onClick={() => applyVoucherPick(voucher)}
                    className="text-primary font-medium text-[14px] ml-auto cursor-pointer"
                  >
                    Áp dụng
                  </div>
                </div>
              </div>
            </div>
          ))}
          {vouchers.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              Không có mã giảm giá nào
            </div>
          )}
        </div>
      </Drawer>

      {selectedCard && (
        <Button
          type="primary"
          loading={loading}
          onClick={handlePayment}
          disabled={!emailRecipient.trim() || !!emailError}
          className="w-full !rounded-xl !shadow-lg !py-4 !h-auto"
        >
          <span className="text-white font-bold text-lg">
            Thanh toán {formatVND(Math.max(0, selectedCard.productamount * quantity - voucherValue))}
          </span>
        </Button>
      )}
    </div>
  );
};

export default GameCard;